import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Loader, LogOut, ChefHat, ShieldOff } from 'lucide-react';
import toast from 'react-hot-toast';

const RestaurantSelectionPage = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token          = localStorage.getItem('staffToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchRestaurants();
  }, [navigate]);

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      // ── STEP 1: Use restaurants stored in localStorage during login ──────────
      // /staff-login returns restaurants[] so no extra network call is needed.
      const cached = localStorage.getItem('restaurants');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            console.log('📋 Loaded restaurants from localStorage:', parsed);
            // Filter by subscription plans
            const filtered = await filterRestaurantsBySubscription(parsed);
            setRestaurants(filtered);
            setLoading(false);
            return;
          }
        } catch (_) {
          // corrupt cache — fall through to network call
        }
      }

      // ── STEP 2: Fetch from staff-aware endpoint (fallback) ───────────────────
      const token = localStorage.getItem('staffToken');
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/billing-pos/auth/staff/my-restaurants`;

      console.log('🔗 Fetching restaurants from:', apiUrl);

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Restaurant data received:', data);

        // Handle both array and wrapped responses
        let restaurantList = [];
        if (Array.isArray(data)) {
          restaurantList = data;
        } else if (data.restaurants && Array.isArray(data.restaurants)) {
          restaurantList = data.restaurants;
        } else if (data.data && Array.isArray(data.data)) {
          restaurantList = data.data;
        }

        console.log('📋 Parsed restaurants:', restaurantList);
        // Filter by subscription plans
        const filtered = await filterRestaurantsBySubscription(restaurantList);
        setRestaurants(filtered);

        if (filtered.length === 0) {
          toast.error('कोई भी restaurant active subscription के साथ नहीं है। कृपया admin से संपर्क करें।', {
            duration: 5000,
            style: { background: '#ef4444', color: '#fff', fontSize: '14px', fontWeight: '600' }
          });
        }
      } else if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || 'Failed to load restaurants');
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        toast.error('🔌 Cannot connect to server. Backend is not running or unreachable.', { duration: 5000 });
      } else if (error.message.includes('NetworkError') || error.message.includes('Network request failed')) {
        toast.error('🌐 Network error. Please check your internet connection.', { duration: 5000 });
      } else {
        toast.error('❌ Failed to load restaurants. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter restaurants by checking if they have POS Captain App plan
  const filterRestaurantsBySubscription = async (restaurantList) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';
    const token = localStorage.getItem('staffToken');
    
    // Check only POS Captain App plan (ID: 7) for staff ordering
    const POS_CAPTAIN_APP_PLAN_ID = 7;
    
    try {
      const filtered = [];
      
      for (const restaurant of restaurantList) {
        const restaurantId = restaurant.restaurant_id || restaurant.restaurantId || restaurant.id;
        if (!restaurantId) continue;
        
        try {
          // Use restaurant-scoped endpoint to check only THIS restaurant's direct subscriptions
          // (not owner-wide fallback subscriptions)
          const response = await fetch(
            `${API_BASE}/api/billing-pos/subscriptions/check/restaurant-scoped?restaurantId=${restaurantId}&planId=${POS_CAPTAIN_APP_PLAN_ID}`,
            {
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` })
              }
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.hasAccess || data.active) {
              filtered.push(restaurant);
              console.log(`✅ Restaurant ${restaurantId} has POS Captain App plan`);
            } else {
              console.log(`⚠️ Restaurant ${restaurantId} - POS Captain App plan not active`);
            }
          }
        } catch (err) {
          console.warn(`Could not check POS Captain App plan for restaurant ${restaurantId}:`, err);
        }
      }
      
      console.log(`📊 Filtered ${restaurantList.length} restaurants down to ${filtered.length} with POS Captain App`);
      return filtered;
    } catch (error) {
      console.error('Error filtering restaurants by subscription:', error);
      // Fallback: return all restaurants if check fails
      return restaurantList;
    }
  };

  const handleSelectRestaurant = async (restaurant) => {
    console.log('✅ Selected restaurant:', restaurant);
    
    // Try different field name variations (backend uses snake_case)
    const restaurantId = restaurant.restaurant_id || restaurant.restaurantId || restaurant.id;
    const restaurantName = restaurant.restaurant_name || restaurant.restaurantName || restaurant.name || 'Unknown Restaurant';
    
    if (!restaurantId) {
      console.error('❌ Restaurant ID not found in:', restaurant);
      toast.error('Invalid restaurant data');
      return;
    }

    const isOwner         = localStorage.getItem('isOwner') === 'true';

    // Both owner and staff: just store the selected restaurant.
    localStorage.setItem('restaurantId', String(restaurantId));

    // Store selected restaurant name for display
    localStorage.setItem('selectedRestaurantId',   String(restaurantId));
    localStorage.setItem('selectedRestaurantName', restaurantName);
    
    console.log('💾 Stored restaurant:', { id: restaurantId, name: restaurantName });
    
    toast.success(`Selected: ${restaurantName}`);
    navigate('/tables');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Select Restaurant</h1>
              <p className="text-sm text-gray-600">{localStorage.getItem('staffName') || localStorage.getItem('ownerEmail') || 'Staff'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Restaurants Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading restaurants...</p>
            </div>
          </div>
        ) : accessDenied ? (
          // ── Access Denied ──────────────────────────────────────────────────
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <ShieldOff className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">You don't have access</h2>
            <p className="text-gray-500 mb-2 max-w-sm">
              Staff Order permission is not enabled for your account.
            </p>
            <p className="text-gray-400 text-sm mb-8">
              Please contact your manager to enable the permission.
            </p>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors"
            >
              <LogOut size={18} />
              Go Back to Login
            </button>
          </div>
        ) : restaurants.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <ShieldOff className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">You don't have access</h2>
            <p className="text-gray-500 mb-2 max-w-sm">
              {localStorage.getItem('isOwner') === 'true'
                ? 'No active restaurants found on your account.'
                : 'Staff Order permission is not enabled for your account.'}
            </p>
            <p className="text-gray-400 text-sm mb-8">
              Please contact your manager to get access.
            </p>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors"
            >
              <LogOut size={18} />
              Go Back to Login
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {restaurants.map((restaurant, index) => {
              // Backend returns snake_case field names
              const restaurantId = restaurant.restaurant_id || restaurant.restaurantId || restaurant.id;
              const restaurantName = restaurant.restaurant_name || restaurant.restaurantName || restaurant.name || 'Unnamed Restaurant';
              const restaurantAddress = restaurant.address || restaurant.restaurant_address || '';
              const restaurantPhone = restaurant.contact_phone || restaurant.phone || restaurant.phoneNumber || restaurant.phone_number || '';
              // Treat as active unless backend explicitly marks it inactive.
              // Login response restaurants[] has only {restaurantId, restaurantName, restaurantAddress, cuisineType}
              // — no is_active field — those restaurants must always be selectable.
              const isActive = restaurant.is_active !== false && restaurant.isActive !== false;
              
              return (
                <button
                  key={restaurantId || index}
                  onClick={() => handleSelectRestaurant(restaurant)}
                  className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 p-6 border-2 border-transparent hover:border-orange-500 text-left group ${
                    !isActive ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isActive === false}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Store className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-800 mb-1 truncate">
                        {restaurantName}
                      </h3>
                      {restaurantAddress && (
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {restaurantAddress}
                        </p>
                      )}
                      {restaurantPhone && (
                        <p className="text-xs text-gray-500">
                          📞 {restaurantPhone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-orange-600 font-semibold text-sm group-hover:text-orange-700">
                      Select Restaurant →
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantSelectionPage;
